/*
 * @Description: 商品附加选项本地存储
 *
 * 存储结构：
 * {
 *   [productCode1]: {
 *     [optionCode1]: boolean,
 *     [optionCode2]: boolean,
 *     ...
 *   },
 *   [productCode2]: {
 *     [optionCode1]: boolean,
 *     [optionCode2]: boolean,
 *     ...
 *   },
 *   ...
 * }
 *
 * 只有用户手动操作过的 optionCode 才会出现在 map 中；未操作过的 optionCode 不写入。
 */
import type { CommodityRelatedOptionCode } from '@/pages/order-center/type';
import getCache from '@hll/cache';

/** 单个商品下用户操作过的开关状态：仅包含被显式拨动过的 code */
export type ProductOptionsStatus = Partial<Record<CommodityRelatedOptionCode, boolean>>;
/** userId 级全量 map：productCode -> 用户操作过的 optionCode 状态 */
export type OptionsMap = Record<string, ProductOptionsStatus>;

const STORAGE_KEY_PREFIX = 'commodity_related_options';

const storage = getCache().local;

function buildKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}_${userId}`;
}

/** 读取指定用户的全量选项 map */
export function readOptionsMap(userId: string): OptionsMap | null {
  if (!userId) return null;
  return storage?.getItem(buildKey(userId)) || null;
}

/** 将指定用户的全量选项 map 写入本地缓存 */
export function writeOptionsMap(userId: string, map: OptionsMap): void {
  if (!userId) return;
  storage?.setItem(buildKey(userId), map);
}

/**
 * 单条增量写入：用户拨动一次开关时调用，只覆盖对应商品的对应 code，
 * 不影响其他商品、也不影响同一商品下其他 code。
 */
export function writeProductOption(
  userId: string,
  productCode: string,
  optionCode: CommodityRelatedOptionCode,
  value: boolean,
): void {
  if (!userId || !productCode || !optionCode) return;
  const map = readOptionsMap(userId) || {};
  const next: ProductOptionsStatus = { ...(map[productCode] || {}), [optionCode]: value };
  map[productCode] = next;
  writeOptionsMap(userId, map);
}
